import type { InvoiceLineItem, InvoicePreviewLine } from '@/hooks/useInvoices';
import {
  legacyLaborHourlyUnitPriceCents,
  legacyLaborPieceDescription,
  lineNetAfterDiscountPrecise,
} from '@/utils/money';
import type {
  BookingServiceExtra,
  BookingServiceLaborItem,
  BookingServicePart,
  WorkOrderDetail,
  WorkOrderItem,
} from '@/hooks/useWorkOrders';
import { distinctServiceTitle } from '../serviceTitle';
import { isFixedPricePackage } from '../fixedPricePackage';

/** GET /work-orders/:id extras for a fixed-price override (#1983). */
export type BillingServiceSnapshot = WorkOrderDetail['services'][number] & {
  pricingType?: string;
  fixedPriceCents?: number | null;
  fixedPriceOverrideCents?: number | null;
  fixedPriceOverrideGrossCents?: number | null;
  catalogFixedPriceGrossCents?: number | null;
};

export type BillingLine = InvoiceLineItem | InvoicePreviewLine;

export interface BillingLineCapabilities {
  quantity: boolean;
  unitPrice: boolean;
  discount: boolean;
  deletable: boolean;
}

export type BillingLineSource =
  | {
      kind: 'labor';
      bookingServiceId: string;
      laborItemId: string;
      capabilities: BillingLineCapabilities;
    }
  | {
      /**
       * One labour line summing every step of a service (#2375). Locked in the
       * Payments card: hours and discounts belong to the steps, which are
       * edited in the Works card (#2638).
       */
      kind: 'serviceLabor';
      bookingServiceId: string;
      laborItemIds: readonly string[];
      capabilities: BillingLineCapabilities;
    }
  | {
      kind: 'part';
      bookingServiceId: string;
      partId: string;
      capabilities: BillingLineCapabilities;
    }
  | {
      kind: 'consumedPart';
      partId: string;
      capabilities: BillingLineCapabilities;
    }
  | {
      kind: 'extra';
      bookingServiceId: string;
      extraId: string;
      capabilities: BillingLineCapabilities;
    }
  | {
      kind: 'fixedService';
      bookingServiceId: string;
      capabilities: BillingLineCapabilities;
      overrideActive: boolean;
    }
  | {
      kind: 'workOrderItem';
      itemId: string;
      costField: 'laborCostCents' | 'partsCostCents';
      capabilities: BillingLineCapabilities;
    }
  | {
      kind: 'readonly';
      reason: 'surcharge' | 'unresolved';
      capabilities: BillingLineCapabilities;
    };

/** Stock-consumed `work_order_parts` row. WorkOrderDetail does not carry these. */
export interface WorkOrderConsumedPart {
  id: string;
  partName: string;
  quantity: number;
  workOrderItemId?: string | null;
}

export type BillingWorkOrderSnapshot = {
  services: readonly BillingServiceSnapshot[];
  items: WorkOrderDetail['items'];
  consumedParts?: readonly WorkOrderConsumedPart[];
};

export type AnnotatedBillingLine = BillingLine & {
  source?: BillingLineSource;
};

const LABOR_CAPS: BillingLineCapabilities = {
  quantity: true,
  // Per-step labor rate has no endpoint; the work-order hourly rate lives in
  // the settings block at the bottom of the Payments card.
  // TODO(#1959): endpoint puuttuu — työvaiheen oma tuntihinta
  unitPrice: false,
  discount: true,
  deletable: true,
};

const PART_CAPS: BillingLineCapabilities = {
  quantity: true,
  unitPrice: true,
  discount: true,
  deletable: true,
};

const CONSUMED_PART_CAPS: BillingLineCapabilities = {
  // updatePartSchema.quantity is z.number().int().min(1) — a decimal or 0 is 400.
  quantity: true,
  unitPrice: true,
  discount: true,
  deletable: true,
};

const EXTRA_CAPS: BillingLineCapabilities = {
  quantity: true,
  unitPrice: true,
  // TODO(#1959): endpoint puuttuu — lisärivin alennus-%
  discount: false,
  deletable: true,
};

const FIXED_SERVICE_CAPS: BillingLineCapabilities = {
  // Quantity and discount-% stay out of scope (#1983): a fixed price is one
  // piece; line discount is the invoice-level discount if needed.
  quantity: false,
  unitPrice: true,
  discount: false,
  deletable: false,
};

const WORK_ORDER_ITEM_CAPS: BillingLineCapabilities = {
  // Preview emits quantity 1 for legacy work_order_items, or the item's
  // estimated_hours for an hours-billed labor row (#2704). Either way the
  // quantity is not editable here: the stored value is the line net.
  // TODO(#1959): endpoint puuttuu — work_order_items-määrä ja alennus-%
  quantity: false,
  unitPrice: true,
  discount: false,
  deletable: true,
};

const READONLY_CAPS: BillingLineCapabilities = {
  quantity: false,
  unitPrice: false,
  discount: false,
  deletable: false,
};

const READONLY_SURCHARGE: BillingLineSource = {
  kind: 'readonly',
  reason: 'surcharge',
  capabilities: READONLY_CAPS,
};

const READONLY_UNRESOLVED: BillingLineSource = {
  kind: 'readonly',
  reason: 'unresolved',
  capabilities: READONLY_CAPS,
};

/**
 * The combined labour line is never editable here: its quantity is the sum of
 * several steps, so writing it back onto any one step would count the other
 * steps' hours twice (#2638).
 */
const SERVICE_LABOR_CAPS: BillingLineCapabilities = READONLY_CAPS;

/** Source reference the preview sends on a combined labour line (#2638). */
function lineServiceLaborRef(
  line: BillingLine,
): { bookingServiceId: string; laborItemIds: string[] } | undefined {
  const { bookingServiceId, laborItemIds } = line as BillingLine & {
    bookingServiceId?: unknown;
    laborItemIds?: unknown;
  };
  if (typeof bookingServiceId !== 'string' || bookingServiceId.length === 0) return undefined;
  if (!Array.isArray(laborItemIds)) return undefined;
  const ids = laborItemIds.filter((id): id is string => typeof id === 'string');
  return { bookingServiceId, laborItemIds: ids };
}

function partInvoiceDescription(part: BookingServicePart): string {
  return part.notes ? `${part.name} (${part.notes})` : part.name;
}

function laborInvoiceDescription(
  item: BookingServiceLaborItem,
  serviceName: string,
): string {
  return item.description.trim().length > 0 ? item.description : serviceName;
}

/**
 * Preview/invoice DTOs omit workOrderPartId, but the field is present on
 * persisted invoice rows and on assembleInvoice draft lines.
 */
function lineWorkOrderPartId(line: BillingLine): string | undefined {
  const value = (line as BillingLine & { workOrderPartId?: unknown }).workOrderPartId;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function matchConsumedPartByNameAndQuantity(
  line: BillingLine,
  workOrder: BillingWorkOrderSnapshot,
  usedConsumedPartIds: Set<string>,
): BillingLineSource | undefined {
  const consumed = (workOrder.consumedParts ?? []).find((entry) => {
    if (usedConsumedPartIds.has(entry.id)) return false;
    return entry.partName === line.description
      && Number(entry.quantity) === Number(line.quantity);
  });
  if (!consumed) return undefined;
  usedConsumedPartIds.add(consumed.id);
  return {
    kind: 'consumedPart',
    partId: consumed.id,
    capabilities: CONSUMED_PART_CAPS,
  };
}

/**
 * Match a preview/invoice line back onto the work-order entity that produced it.
 * Used IDs are consumed so duplicate descriptions on the same work stay 1:1.
 */
export function resolveBillingLineSource(
  line: BillingLine,
  workOrder: BillingWorkOrderSnapshot,
  used: {
    laborItemIds: Set<string>;
    partIds: Set<string>;
    extraIds: Set<string>;
    itemKeys: Set<string>;
    consumedPartIds: Set<string>;
  },
): BillingLineSource {
  if (line.lineType === 'surcharge' || line.lineType === 'discount') {
    return READONLY_SURCHARGE;
  }

  // The combined labour line names its sources (#2638). Resolve it by id,
  // never by description: its quantity is the service total, not a step's.
  if (line.lineType === 'labor') {
    const ref = lineServiceLaborRef(line);
    // One contributing step: the line's hours and discount are that step's,
    // so it stays editable per step, as without the reference.
    const [onlyLaborItemId] = ref?.laborItemIds ?? [];
    if (ref?.laborItemIds.length === 1 && onlyLaborItemId) {
      used.laborItemIds.add(onlyLaborItemId);
      return {
        kind: 'labor',
        bookingServiceId: ref.bookingServiceId,
        laborItemId: onlyLaborItemId,
        capabilities: LABOR_CAPS,
      };
    }
    if (ref) {
      for (const id of ref.laborItemIds) used.laborItemIds.add(id);
      return {
        kind: 'serviceLabor',
        bookingServiceId: ref.bookingServiceId,
        laborItemIds: ref.laborItemIds,
        capabilities: SERVICE_LABOR_CAPS,
      };
    }
  }

  // Stable id wins over description matching. Preview currently strips this
  // field; persisted invoice rows and future preview payloads may still send it.
  if (line.lineType === 'part') {
    const fromLineId = lineWorkOrderPartId(line);
    if (fromLineId && !used.consumedPartIds.has(fromLineId)) {
      used.consumedPartIds.add(fromLineId);
      return {
        kind: 'consumedPart',
        partId: fromLineId,
        capabilities: CONSUMED_PART_CAPS,
      };
    }
  }

  const groupName = line.groupName?.trim() ?? '';
  // Groups are named by the catalog name, so two works of one product share a
  // name (#3655); the group order is the service index and tells them apart.
  const orderedService = line.groupSortOrder != null ? workOrder.services[line.groupSortOrder] : undefined;
  const service = groupName.length === 0
    ? undefined
    : orderedService?.name === groupName
      ? orderedService
      : workOrder.services.find((entry) => entry.name === groupName);

  if (service) {
    // A line without the reference (a stored invoice row, or an API older than
    // #2638) that carries the service name while the service has several
    // steps is the combined line too. Matching it to one step by name is the
    // double-hours bug, so it stays locked.
    if (
      line.lineType === 'labor'
      && line.description === service.name
      && service.laborItems.length > 1
    ) {
      const laborItemIds = service.laborItems.map((item) => item.id);
      for (const id of laborItemIds) used.laborItemIds.add(id);
      return {
        kind: 'serviceLabor',
        bookingServiceId: service.bookingServiceId,
        laborItemIds,
        capabilities: SERVICE_LABOR_CAPS,
      };
    }

    if (line.lineType === 'labor') {
      const laborItem = service.laborItems.find((item) => {
        if (used.laborItemIds.has(item.id)) return false;
        return laborInvoiceDescription(item, service.name) === line.description;
      });
      if (laborItem) {
        used.laborItemIds.add(laborItem.id);
        return {
          kind: 'labor',
          bookingServiceId: service.bookingServiceId,
          laborItemId: laborItem.id,
          capabilities: LABOR_CAPS,
        };
      }
    }

    if (line.lineType === 'part') {
      const part = service.parts.find((item) => {
        if (used.partIds.has(item.id)) return false;
        return partInvoiceDescription(item) === line.description;
      });
      if (part) {
        used.partIds.add(part.id);
        return {
          kind: 'part',
          bookingServiceId: service.bookingServiceId,
          partId: part.id,
          capabilities: PART_CAPS,
        };
      }
    }

    if (line.lineType === 'service') {
      const extra = service.extras.find((item) => {
        if (used.extraIds.has(item.id)) return false;
        return item.description === line.description;
      });
      if (extra) {
        used.extraIds.add(extra.id);
        return {
          kind: 'extra',
          bookingServiceId: service.bookingServiceId,
          extraId: extra.id,
          capabilities: EXTRA_CAPS,
        };
      }
      if (line.description === service.name) {
        return {
          kind: 'fixedService',
          bookingServiceId: service.bookingServiceId,
          capabilities: FIXED_SERVICE_CAPS,
          overrideActive: service.fixedPriceOverrideCents != null,
        };
      }
    }
  }

  if (line.lineType === 'part') {
    const consumed = matchConsumedPartByNameAndQuantity(line, workOrder, used.consumedPartIds);
    if (consumed) return consumed;
  }

  const costField: 'laborCostCents' | 'partsCostCents' =
    line.lineType === 'part' ? 'partsCostCents' : 'laborCostCents';
  const itemKey = (itemId: string) => `${itemId}:${costField}`;
  const item = workOrder.items.find((entry) => {
    if (used.itemKeys.has(itemKey(entry.id))) return false;
    if (entry[costField] <= 0) return false;
    const name = entry.description;
    if (groupName.length > 0 && groupName !== name) return false;
    if (line.description === name) return true;
    // A lump-sum labor row carries its hours in the text (#2704).
    const hours = Number(entry.estimatedHours ?? 0);
    return costField === 'laborCostCents'
      && hours > 0
      && line.description === legacyLaborPieceDescription(name, hours);
  });
  if (item) {
    used.itemKeys.add(itemKey(item.id));
    return {
      kind: 'workOrderItem',
      itemId: item.id,
      costField,
      capabilities: WORK_ORDER_ITEM_CAPS,
    };
  }

  return READONLY_UNRESOLVED;
}

export function annotateBillingLines(
  lines: readonly BillingLine[],
  workOrder: BillingWorkOrderSnapshot,
  options?: { includeUnmatchedItems?: boolean; fallbackTaxPercent?: number },
): AnnotatedBillingLine[] {
  const used = {
    laborItemIds: new Set<string>(),
    partIds: new Set<string>(),
    extraIds: new Set<string>(),
    itemKeys: new Set<string>(),
    consumedPartIds: new Set<string>(),
  };
  const annotated: AnnotatedBillingLine[] = lines.map((line) => ({
    ...line,
    source: resolveBillingLineSource(line, workOrder, used),
  }));

  if (!options?.includeUnmatchedItems) return annotated;

  const fallbackTaxPercent = options.fallbackTaxPercent ?? 0;
  for (const item of workOrder.items) {
    const laborKey = `${item.id}:laborCostCents`;
    const partsKey = `${item.id}:partsCostCents`;
    if (used.itemKeys.has(laborKey) || used.itemKeys.has(partsKey)) continue;
    used.itemKeys.add(laborKey);
    annotated.push(manualItemPreviewLine(item, fallbackTaxPercent));
  }
  return annotated;
}

function manualItemPreviewLine(
  item: WorkOrderItem,
  fallbackTaxPercent: number,
): AnnotatedBillingLine {
  const lineNetCents = item.laborCostCents > 0 ? item.laborCostCents : item.partsCostCents;
  const costField: 'laborCostCents' | 'partsCostCents' =
    item.laborCostCents > 0 || item.partsCostCents <= 0 ? 'laborCostCents' : 'partsCostCents';
  // Same shape as the preview's legacy row (#2704): hours × derived rate when
  // the item carries estimated_hours and the net divides exactly.
  const hours = Number(item.estimatedHours ?? 0);
  const hourlyUnitPriceCents = costField === 'laborCostCents'
    ? legacyLaborHourlyUnitPriceCents(item.laborCostCents, hours)
    : null;
  return {
    lineType: costField === 'partsCostCents' ? 'part' : 'labor',
    description: costField === 'laborCostCents' && hourlyUnitPriceCents == null && hours > 0
      ? legacyLaborPieceDescription(item.description, hours)
      : item.description,
    quantity: hourlyUnitPriceCents != null ? hours : 1,
    unit: hourlyUnitPriceCents != null ? 'hour' : 'piece',
    unitPriceCents: hourlyUnitPriceCents ?? lineNetCents,
    discountPercent: 0,
    lineTotalCents: lineNetCents,
    taxable: true,
    taxPercent: fallbackTaxPercent,
    groupName: null,
    source: {
      kind: 'workOrderItem',
      itemId: item.id,
      costField,
      capabilities: WORK_ORDER_ITEM_CAPS,
    },
  };
}

/**
 * Net `laborCostCents`/`partsCostCents` to store when a legacy item line's
 * unit price is edited. The column holds the LINE net: an hours-billed labor
 * row (#2704) shows a per-hour price, so the typed rate is multiplied by the
 * line's hours; a one-piece row stores the unit price as is.
 */
export function workOrderItemCostFromUnitNet(
  line: Pick<BillingLine, 'quantity' | 'unit'>,
  costField: 'laborCostCents' | 'partsCostCents',
  unitNetCents: number,
): number {
  if (costField === 'laborCostCents' && line.unit === 'hour' && line.quantity > 0) {
    return lineNetAfterDiscountPrecise(unitNetCents, line.quantity, 0);
  }
  return unitNetCents;
}

export function findServiceByBookingId(
  workOrder: Pick<WorkOrderDetail, 'services'>,
  bookingServiceId: string,
): WorkOrderDetail['services'][number] | undefined {
  return workOrder.services.find((service) => service.bookingServiceId === bookingServiceId);
}

export function findServiceByName(
  workOrder: Pick<WorkOrderDetail, 'services'>,
  name: string,
  groupSortOrder?: number,
): WorkOrderDetail['services'][number] | undefined {
  // Two works of one product share the catalog name; the group order tells them apart (#3655).
  const ordered = groupSortOrder != null ? workOrder.services[groupSortOrder] : undefined;
  return ordered?.name === name ? ordered : workOrder.services.find((service) => service.name === name);
}

export type ServiceDetailsLaborItem = {
  id?: string;
  description: string;
  estimatedHours: number;
  discountPercent: number;
  sortOrder: number;
};

export type ServiceDetailsPart = {
  id?: string;
  partCategoryId: string | null;
  name: string;
  isRequired: boolean;
  quantity: number;
  unitPriceGrossCents: number;
  vatPercent: number;
  discountPercent: number;
  notes: string | null;
  sortOrder: number;
};

export type ServiceDetailsExtra = {
  id?: string;
  description: string;
  amountGrossCents: number;
  isTaxable: boolean;
  hasVariableQuantity: boolean;
  unit: string | null;
  quantity: number | null;
  sortOrder: number;
};

export type ServiceDetailsPayload = {
  /** Work-specific title; null clears it (#3655). */
  title?: string | null;
  notes?: string | null;
  laborItems?: ServiceDetailsLaborItem[];
  parts?: ServiceDetailsPart[];
  extras?: ServiceDetailsExtra[];
  /** Typed gross. `null` clears the override back to catalog (#1983). */
  fixedPriceOverrideGrossCents?: number | null;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function mapLaborItems(
  items: BookingServiceLaborItem[],
  mutateId: string | null,
  patch: { estimatedHours?: number; discountPercent?: number },
  remove: boolean,
): ServiceDetailsLaborItem[] {
  return items
    .filter((item) => !(remove && item.id === mutateId))
    .map((item, index) => {
      const next: ServiceDetailsLaborItem = {
        description: item.description,
        estimatedHours:
          item.id === mutateId && patch.estimatedHours !== undefined
            ? patch.estimatedHours
            : item.estimatedHours,
        discountPercent:
          item.id === mutateId && patch.discountPercent !== undefined
            ? patch.discountPercent
            : item.discountPercent,
        sortOrder: index,
      };
      if (UUID_RE.test(item.id)) next.id = item.id;
      return next;
    });
}

function mapParts(
  items: BookingServicePart[],
  mutateId: string | null,
  patch: { quantity?: number; unitPriceGrossCents?: number; discountPercent?: number },
  remove: boolean,
): ServiceDetailsPart[] {
  return items
    .filter((item) => !(remove && item.id === mutateId))
    .map((item, index) => ({
      id: item.id,
      partCategoryId: item.partCategoryId,
      name: item.name,
      isRequired: item.isRequired,
      quantity:
        item.id === mutateId && patch.quantity !== undefined ? patch.quantity : item.quantity,
      unitPriceGrossCents:
        item.id === mutateId && patch.unitPriceGrossCents !== undefined
          ? patch.unitPriceGrossCents
          : item.unitPriceGrossCents,
      vatPercent: item.vatPercent,
      discountPercent:
        item.id === mutateId && patch.discountPercent !== undefined
          ? patch.discountPercent
          : item.discountPercent,
      notes: item.notes,
      sortOrder: index,
    }));
}

function mapExtras(
  items: BookingServiceExtra[],
  mutateId: string | null,
  patch: { quantity?: number | null; amountGrossCents?: number },
  remove: boolean,
): ServiceDetailsExtra[] {
  return items
    .filter((item) => !(remove && item.id === mutateId))
    .map((item, index) => ({
      id: item.id,
      description: item.description,
      amountGrossCents:
        item.id === mutateId && patch.amountGrossCents !== undefined
          ? patch.amountGrossCents
          : item.amountGrossCents,
      isTaxable: item.isTaxable,
      hasVariableQuantity: item.hasVariableQuantity,
      unit: item.unit,
      quantity:
        item.id === mutateId && patch.quantity !== undefined ? patch.quantity : item.quantity,
      sortOrder: index,
    }));
}

export function serviceDetailsPayload(
  service: WorkOrderDetail['services'][number],
): ServiceDetailsPayload {
  return {
    ...('fixedPriceOverrideGrossCents' in service ? { fixedPriceOverrideGrossCents: (service as BillingServiceSnapshot).fixedPriceOverrideGrossCents } : {}),
    title: distinctServiceTitle(service.name, service.title),
    notes: service.notes,
    // A fixed-price package's labor rows are locked (#3655): never sent.
    ...(isFixedPricePackage(service) ? {} : { laborItems: mapLaborItems(service.laborItems, null, {}, false) }),
    parts: mapParts(service.parts, null, {}, false),
    extras: mapExtras(service.extras, null, {}, false),
  };
}

export function payloadAfterLaborPatch(
  service: WorkOrderDetail['services'][number],
  laborItemId: string,
  patch: { estimatedHours?: number; discountPercent?: number },
  remove = false,
): ServiceDetailsPayload {
  const base = serviceDetailsPayload(service);
  // Editing a line does not request a fixed-price override change or its invoice gate.
  delete base.fixedPriceOverrideGrossCents;
  return {
    ...base,
    laborItems: mapLaborItems(service.laborItems, laborItemId, patch, remove),
  };
}

export function payloadAfterPartPatch(
  service: WorkOrderDetail['services'][number],
  partId: string,
  patch: { quantity?: number; unitPriceGrossCents?: number; discountPercent?: number },
  remove = false,
): ServiceDetailsPayload {
  const base = serviceDetailsPayload(service);
  // Editing a line does not request a fixed-price override change or its invoice gate.
  delete base.fixedPriceOverrideGrossCents;
  return {
    ...base,
    parts: mapParts(service.parts, partId, patch, remove),
  };
}

export function payloadAfterExtraPatch(
  service: WorkOrderDetail['services'][number],
  extraId: string,
  patch: { quantity?: number | null; amountGrossCents?: number },
  remove = false,
): ServiceDetailsPayload {
  const base = serviceDetailsPayload(service);
  // Editing a line does not request a fixed-price override change or its invoice gate.
  delete base.fixedPriceOverrideGrossCents;
  return {
    ...base,
    extras: mapExtras(service.extras, extraId, patch, remove),
  };
}

export function payloadAfterAddExtra(
  service: WorkOrderDetail['services'][number],
  extra: ServiceDetailsExtra,
): ServiceDetailsPayload {
  const base = serviceDetailsPayload(service);
  delete base.fixedPriceOverrideGrossCents;
  const extras = base.extras ?? [];
  return {
    ...base,
    extras: [...extras, { ...extra, sortOrder: extras.length }],
  };
}

/** Price-only body — omits collections so they are not replaced (#1983). */
export function payloadAfterFixedPriceOverride(
  fixedPriceOverrideGrossCents: number | null,
): ServiceDetailsPayload {
  return { fixedPriceOverrideGrossCents };
}
